import { useNavigate } from 'react-router-dom';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Switch, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { Interweave, Node } from 'interweave';
import AddIcon from '@mui/icons-material/Add';
import SingleArticleProps from '../../interfaces/props/SingleArticleProps';
import { setTags } from '../../../store/filters/filters.slice';
import classes from './SingleArticle.module.scss';
import URLHelper from '../../helpers/url.helper';
import VariationsModal from '../variations-modal/VariationsModal';
import StringHelper from '../../helpers/string.helper';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Tag } from '../../interfaces/Tag';
import { RouteNames } from '../../enums/RouteNames';
import TagsService from '../../services/tags.service';
import Tags from '../../views/tags/Tags';
import ArticlePreview from '../../views/article-preview/ArticlePreview';
import InformationSection from '../../views/Information-section/InformationSection';

const SingleArticle = ({ article }: SingleArticleProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [showRawContent, setShowRawContent] = useState<boolean>(false);

  const onCreateVariationClickHandler = () => {
    setOpenModal(true);
  };

  const transformHandler = useCallback((node: HTMLElement, children: Node[]): ReactNode => {
    const imageSource = node.getAttribute('src');

    if ('img' === node.tagName.toLocaleLowerCase() && imageSource && !URLHelper.isValidUrl(imageSource)) {
      const imageAlt = node.getAttribute('alt') ?? '';

      return <img src={`${article?.getSiteUrl()}${imageSource}`} alt={imageAlt} />
    }
  }, [article])

  const tagClickHandler = useCallback((tag: Tag) => {
    dispatch(setTags([tag]));
    navigate(RouteNames.HOME);
  }, [dispatch, navigate]);

  const renderInformation = useMemo(() => {
    return (
      <Box>
        <Box className={'d-flex flex-column gap-4 pt-5'}>
          <Box>
            <InformationSection title="Facts" content={article.getFacts()} biggerFont={true} />
          </Box>
          <Box>
            <InformationSection title="Tips And Predictions" content={article.getTipsAndPredictions()} biggerFont={true} />
          </Box>
          <Box>
            <InformationSection title="TV" content={article.getTvChannels()} biggerFont={true} />
          </Box>
        </Box>
        {article.getStories().length === 0 ?
          <Box className='d-flex justify-content-center my-1 justify-content-sm-end'>
            <Button
              className={`${classes['variation-button']} p-2`}
              disableElevation variant='contained'
              onClick={onCreateVariationClickHandler}
            >
              <AddIcon fontSize='small' />
              Create Variation
            </Button>
          </Box> : <></>
        }
      </Box>
    );
  }, [article]);

  const renderAccordion = useMemo(() => {
    return (
      <Accordion className={`${classes['accordion-box']} py-3 ps-2 overflow-hidden`}
        sx={{
          '&:before': {
            display: 'none',
          }
        }}
        elevation={0}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography className='h4'>
            {article.getStories().length === 0 ? 'Original Article' : 'Video Transcript'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {article.getStories().length === 0 ?
            <Box className='d-flex align-items-center justify-content-start pb-2'>
              <Switch classes={{ checked: 'switch', track: 'switch-base' }} checked={showRawContent} onChange={() => setShowRawContent(!showRawContent)} />
              <Typography>
                Raw Content
              </Typography>
            </Box> : <></>
          }
          <Typography
            variant='h4'
            align='left'
            fontWeight='bold'
          >
            {article.getHeadlinesOriginal()}
          </Typography>
          {
            showRawContent ? (
              <Interweave content={article.getRawContent()} transform={transformHandler} />
            ) : (
              <Interweave content={article.getClearedHTMLRawContent()} />
            )
          }
        </AccordionDetails>
      </Accordion>)
  }, [article, showRawContent, transformHandler]);

  return (
    <Box className='d-flex flex-column gap-4'>
      <Box className={`${classes['article-box']} p-1 p-sm-4`}>
        <ArticlePreview
          image={article.getAssets()}
          title={article.getHeadlinesEnglish()}
          summary={article.getProcessedContent()}
          createdAt={article.getPostCreatedAt() ?? article.getCreatedAt()}
          siteName={article.getSiteName()}
          author={article.getAuthor()}
          siteURL={article.getSiteUrl()}
          articleURL={article.getUrl()}
          numberOfWords={article.getNumberOfWords()}
          channel={article.getChannelName()}
          iFrameURL={article.getIframeURL()}
          videoLength={article.getVideoLength()}
        />
        {renderInformation}
      </Box>
      <Box className={`${classes['tags-box']} p-4`}>
        <Typography className='h4 ps-1 pb-4'>
          Article Tags
        </Typography>
        <Tags tags={TagsService.clearNoTagTags(article?.getTags())} onTagClick={tagClickHandler} />
      </Box>
      {renderAccordion}
      <VariationsModal key={StringHelper.generateRandomString(10)} isOpen={openModal} onClose={() => setOpenModal(false)} contentPiece={article} />
    </Box>
  );
};

export default SingleArticle;
